import { Rectangle } from "../../Utils/Shapes/Rectangle";
import { Section as WorldSection } from "../Game World/Sections/Section";
import { Node } from "../../Utils/Collections/Node";
import { Shield } from "./Shield";
import { Game, eModelAsset, eAtlas_0, rnd } from "../../Game";
import { GameWorld } from "../Game World/GameWorld";
import { Parcel, eParcelState as ePackageState } from "../Parcel/Parcel";
import { Gameplay, TIMER, eBatchGroup } from "../Gameplay";
import { Player, ePlayerState } from "./Player";
import { NodeList } from "../../Utils/Collections/NodeList";
import { DeliveryPoint } from "../Parcel/DeliveryPoint";
import { RND } from "../../Utils/RND";
import { Dispenser } from "../Parcel/Dispenser";
import { ParcelManager } from "../Parcel/ParcelManager";
import { ModelUtils } from "../../Utils/ModelUtils";
import { TextureAtlasUtils } from "../../Utils/TextuAtlasUtils";



export const enum eCharacterState {
    idle,
    walk,
    death,
    inactive
}



/**
 * Vycet duvodu smrti postavicky
 */
export const enum eDeathReason {
    /**
     * Kolize s prekazkou
     */
    collision,
    /**
     * Utonuti
     */
    drowning,
}



const enum eFormationPosFlag {
    top = 1,
    bottom = 2,
}



export class Character {
    private static readonly WALK_SWAY_DIS = 0.02;
    private static readonly WALK_SWAY_TIME = 100;
    static readonly WALK_Y = 0.03;

    private static readonly EMOJI_OFFSET_Y = 0.55;

    /**
     * Udalost vyvolana pri smrti postavy
     * Emitter: Gameplay.events
     * Parametry: character: Character, deathReason: eDeathReason
     */
    static readonly EVENT_DEATH = "char:death";

    /**
     * Udalost vyvolana po sebrani balicku hracem
     * Emitter: Gameplay.events
     * Parametry: character: Character
     */
    static readonly EVENT_PACKAGE_PICKUP = "char:pkgPickup";

    private static _TMP_VEC3: pc.Vec3 = new pc.Vec3();

    /**
     * (RO) Hrac, kteremu postavicky patri
     */
    get player(): Player { return this._player; }
    private _player: Player;

    /**
     * (RO) Index postavy
     */
    get id(): number { return this._id; }
    private _id: number;

    /**
     * pc.Entity s postavou hrace
     */
    private _entity: pc.Entity;

    /**
     * pc.Entity pro emoji ikonu
     */
    private _emoji: pc.Entity;

    private static readonly SHADOW_OFFSET_X = 0.02;
    private static readonly SHADOW_OFFSET_Z = 0;
    private static readonly SHADOW_W = 0.3;
    private static readonly SHADOW_H = 0.3;

    /**
     * pc.Entity pro "falesny" stin
     */
    private _shadow: pc.Entity;

    private static _MATERIALS1: pc.StandardMaterial[];
    private static _MATERIALS2: pc.StandardMaterial[];

    /**
     * (RO) Aktualni stav postavy
     */
    get state(): eCharacterState { return this._state; }
    private _state: eCharacterState;

    /**
     * Update funkce pro jednotlive stavy hrace
     */
    private static _STATE_UPDATE_FNC: (() => void)[];

    private _time: number;

    /**
     * (RO) Aktualni pozice postavy
     */
    get position(): pc.Vec3 { return this._pos; }
    private _pos: pc.Vec3;

    /**
     * Offset postavy od horniho okraje formace (v poctu postav, ktere jsou pred touto postavou)
     */
    formationTopOffset: number;

    /**
     * Offset postavy od dolniho okraje formace (v poctu postav, ktere jsou pred touto postavou)
     */
    formationBotOffset: number;

    /**
     * (RO) Smer postavy na ose Z
     */
    get dir(): number { return this._dir; }
    private _dir: number;

    //#region Kolizni obdelnik
    private static readonly COLRC_X = -0.1;
    private static readonly COLRC_Y = -0.1;
    private static readonly COLRC_W = 0.2;
    private static readonly COLRC_H = 0.2;

    /**
     * (RO) Kolizni obdelnik
     */
    get collisionRect(): Rectangle { return this._colRect; }
    private _colRect: Rectangle;
    //#endregion

    //#region Balicky
    /**
     * Offset balicku, ktery hrac nese, od pozice hrace
     */
    private static readonly PKG_Z_OFFSET = 0.17;

    /**
     * Offset balicku, ktery hrac nese na ose Y
     */
    private static readonly PKG_Y_OFFSET = 0.12;

    /**
     * Seznam balicku, ktere hrac nese
     */
    private _pkgList: NodeList<Parcel>;

    /**
     * Maska indexu vydejen, jejichz balicky hrac nese
     */
    private _pkgDispenserMask: number;

    /**
     * (RO) Aktualni pocet balicku, ktere hrac dorucuje
     */
    get packageCnt(): number { return this._pkgList.size; }
    //#endregion

    /**
     * Uzel s prvni sekci herniho sveta, do ktere postava zasahuje
     */
    private _1stSectionNode: Node<WorldSection>;

    private _swayTimeOffset: number;

    /**
     * Stit
     */
    private _shield: Shield;

    /**
     * (RO) True, pokud ma hrac aktivni stit
     */
    get hasShield(): boolean { return this._shield.isActive; }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(player: Player) {
        // Update funkce
        if (!Character._STATE_UPDATE_FNC) {
            Character._STATE_UPDATE_FNC = [];
            Character._STATE_UPDATE_FNC[eCharacterState.walk] = this.updateWalk;
            Character._STATE_UPDATE_FNC[eCharacterState.death] = this.updateDeath;
        }

        this._player = player;
        this._id = -1;
        this._pos = new pc.Vec3();
        this._colRect = new Rectangle();

        // Entity
        this._entity = new pc.Entity();

        this._entity.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.APP.assets.find(eModelAsset.player, pc.eAssetType.model),
            castShadows: true,
            castShadowsLightmap: false,
            batchGroupId: Gameplay.instance.batchGroups[eBatchGroup.dynamic]
        });

        this._entity.enabled = false;
        Game.APP.root.addChild(this._entity);

        // Materialy (prvni material bez opacity a druhy s podporou opacity)
        if (!Character._MATERIALS1) {
            Character._MATERIALS1 = [];
            Character._MATERIALS2 = [];

            ModelUtils.cloneModelMaterials(this._entity.model, {
                blendType: pc.BLEND_NORMAL,
                opacity: 0.1
            }, Character._MATERIALS1, Character._MATERIALS2, Game.APP.assets, "opacity_");
        }

        // Emoji ikona pro smrt postavy
        let atlas = <pc.TextureAtlas>Game.APP.assets.find(eAtlas_0.NAME, pc.eAssetType.atlas).resource;

        this._emoji = new pc.Entity();
        this._emoji.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_IMAGE,
            texture: atlas.texture,
            width: 0.2,
            height: 0.2,
            anchor: [0.5, 0.5, 0.5, 0.5],
            pivot: [0.5, 1]
        });
        this._emoji.setLocalPosition(0, Character.EMOJI_OFFSET_Y, 0);
        this._emoji.element.enabled = false;
        this._entity.addChild(this._emoji);

        // "falesny" stin
        this._shadow = new pc.Entity();
        this._shadow.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_IMAGE,
            texture: atlas.texture,
            width: Character.SHADOW_W,
            height: Character.SHADOW_H,
            opacity: Game.FAKE_SHADOW_OPACITY,
            anchor: [0.5, 0.5, 0.5, 0.5],
            pivot: [0.5, 0.5],
            batchGroupId: Gameplay.instance.batchGroups[eBatchGroup.fakeShadows]
        });
        this._shadow.setLocalEulerAngles(-90, 0, 0);
        this._shadow.element.rect = TextureAtlasUtils.getTexFrameRegion(atlas.texture, (<pc.TextureAtlasFrame>atlas.frames["shadowBox"]).rect);
        this._entity.addChild(this._shadow);

        // Stit
        this._shield = new Shield(this, this._entity);

        // Balicky
        this._pkgList = new NodeList<Parcel>();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    activate(id: number): void {
        if (this._id >= 0)
            return;

        this._id = id;
        this._entity.enabled = true;

        Gameplay.instance.events.on(Player.EVENT_STATE_CHANGE, this.handlePlStateChange, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    deactivate(): void {
        if (this._id < 0)
            return;

        this._id = -1;
        this._entity.enabled = false;
        this._pkgList.clear();
        this._1stSectionNode = undefined;

        Gameplay.instance.events.off(Player.EVENT_STATE_CHANGE, this.handlePlStateChange, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(posX: number, posZ: number, dir: number): void {
        this._state = eCharacterState.idle;
        this._entity.enabled = true;
        this._emoji.element.enabled = false;
        this._shadow.element.enabled = true;

        // Material
        let batchGroupId = Gameplay.instance.batchGroups[eBatchGroup.dynamic];

        if (this._entity.model.batchGroupId != batchGroupId) {
            this._entity.model.meshInstances.forEach((mesh: pc.MeshInstance, i: number) => {
                mesh.setParameter("material_opacity", 1);
                mesh.material = Character._MATERIALS1[i];
            });
            this._entity.model.batchGroupId = batchGroupId;
            this._entity.model.castShadows = true;
        }

        // Pozice a smer pohybu
        this._pos.set(posX, Character.WALK_Y, posZ);
        this._entity.setPosition(this._pos);
        this.setDir(dir);

        // Kolizni obdelnik
        this._colRect.setTo(Character.COLRC_X + this._pos.x, Character.COLRC_Y + this._pos.z, Character.COLRC_W, Character.COLRC_H);

        // Stit
        this._shield.reset();

        // Balicky
        this._pkgList.clear();
        this._pkgDispenserMask = 0;

        // Pohupovani pri chuzi
        this._swayTimeOffset = rnd.randomIntIn(0, Character.WALK_SWAY_TIME - 1);

        // Prvni sekce herniho sveta, do ktere postava zasahuje
        this._1stSectionNode = GameWorld.instance.sections.firstNode;

        while (this._colRect.bottom > this._1stSectionNode.data.bottom)
            this._1stSectionNode = this._1stSectionNode.next;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        let fnc = Character._STATE_UPDATE_FNC[this._state];

        if (fnc)
            fnc.call(this);

        this._shield.update();

        return (this._state != eCharacterState.inactive);
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci pozici balicku, ktery hrac nese
     * @param packageId Index baliku v seznamu balicku hrace
     */
    //-------------------------------------------------------------------------
    getPackagePos(packageId: number): pc.Vec3 {
        Character._TMP_VEC3.set(this._pos.x,
            this._pos.y + Character.PKG_Y_OFFSET + packageId * Parcel.HEIGHT,
            this._pos.z + Character.PKG_Z_OFFSET * this._dir);

        return Character._TMP_VEC3;
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci True, pokud hrac nese balicek z dane vydejny
     * @param dispenser
     */
    //-------------------------------------------------------------------------
    hasPackageFromDispenser(dispenser: Dispenser): boolean {
        return ((this._pkgDispenserMask & (1 << dispenser.id)) != 0);
    }

    //-------------------------------------------------------------------------
    /**
     * Zabiti hrace
     * @param deathReason
     */
    //-------------------------------------------------------------------------
    kill(deathReason: eDeathReason): void {
        if (this._state != eCharacterState.walk)
            throw new Error("Character has been killed already!");

        // Spustit smrt
        this._state = eCharacterState.death;
        this._time = TIMER.time;

        // Vyvolat udalost
        Gameplay.instance.events.fire(Character.EVENT_DEATH, this, deathReason);

        // Uvolnit nesene balicky
        if (!this._pkgList.empty) {
            this._pkgList.forEach((pkg: Parcel) => { pkg.discard(); });
            this._pkgList.clear();
        }
        this._pkgDispenserMask = 0;

        // Zmenit material postavy
        this._entity.model.batchGroupId = -1;
        this._entity.model.meshInstances.forEach((mesh: pc.MeshInstance, i: number) => { mesh.material = Character._MATERIALS2[i]; });

        // Zobrazit EMOJI
        let atlas = <pc.TextureAtlas>Game.APP.assets.find(eAtlas_0.NAME, pc.eAssetType.atlas).resource;

        this._emoji.element.rect = TextureAtlasUtils.getTexFrameRegion(atlas.texture, (<pc.TextureAtlasFrame>atlas.frames["emojiDeath_" + rnd.randomIntIn(0, 4)]).rect);
        this._emoji.element.enabled = true;
        this._emoji.element.opacity = 1;
        this._emoji.setLocalEulerAngles(0, (this._dir < 0 ? 0 : 180), 0);

        // Stin
        this._shadow.element.enabled = false;
    }

    //-------------------------------------------------------------------------
    /**
     * Odevzdat nesene balicky do dodavky
     * @param deliveryPoint
     */
    //-------------------------------------------------------------------------
    deliverPackages(deliveryPoint: DeliveryPoint): void {
        // Odevzdat balicky
        if (!this._pkgList.empty) {
            this._pkgList.forEach((pkg: Parcel) => {
                if (pkg.state == ePackageState.delivering)
                    pkg.deliver(deliveryPoint);
            });

            this._pkgList.clear();
            this._pkgDispenserMask = 0;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Nastavit smer pohybu hrace a pripadne ho posunout na ose Z
     * @param dir Smer pohybu po ose Z
     * @param shift Posunuti na ose Z
     */
    //-------------------------------------------------------------------------
    setDir(dir: number, shift?: number): void {
        if (this._state != eCharacterState.death && dir != this._dir) {
            this._dir = dir;

            if (dir < 0) {
                this._entity.setEulerAngles(0, 0, 0);
                this._shadow.setLocalPosition(Character.SHADOW_OFFSET_X, 0, Character.SHADOW_OFFSET_Z);
            }
            else {
                this._entity.setEulerAngles(0, 180, 0);
                this._shadow.setLocalPosition(-Character.SHADOW_OFFSET_X, 0, Character.SHADOW_OFFSET_Z);
            }

            if (shift != undefined && shift != 0) {
                this._pos.z += shift;
                this._entity.setPosition(this._pos);
            }

            this.updateColRect();
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Overeni kolizi se sekcemi herniho sveta, do kterych postava zasahuje
     */
    //-------------------------------------------------------------------------
    checkCollisions(): void {
        // Zpracovat sekce, se kterymi ma hrac kolizi
        let section = this._1stSectionNode;

        while (true) {
            section.data.checkPlCollisions(this);

            if (this._state == eCharacterState.death)
                break;

            if (this._dir > 0) {
                if (!(section = section.prev) || section.data.bottom <= this._colRect.top)
                    break;
            }
            else if (!(section = section.next) || section.data.top >= this._colRect.bottom) {
                break;
            }
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateWalk(): void {
        // Aktualizovat pozici postavy
        this._pos.z += this._player.walkSpeed * TIMER.deltaS * this._dir;

        let i1 = (TIMER.time - this._player.walkStartTime + this._swayTimeOffset) / Character.WALK_SWAY_TIME;
        let i2 = Math.floor(i1);
        i1 -= i2;

        if ((i2 & 1) == 0) {
            this._pos.y = Character.WALK_Y - Character.WALK_SWAY_DIS * i1;
        }
        else {
            this._pos.y = Character.WALK_Y - Character.WALK_SWAY_DIS + Character.WALK_SWAY_DIS * i1;
        }

        this._entity.setPosition(this._pos);

        // Aktualizovat kolizni obdelnik
        this.updateColRect();

        // Aktualizovat uzel prvni kolizni sekce
        if (this._dir > 0) {
            while (this._colRect.bottom > this._1stSectionNode.data.bottom)
                this._1stSectionNode = this._1stSectionNode.next;
        }
        else {
            while (this._colRect.top < this._1stSectionNode.data.top)
                this._1stSectionNode = this._1stSectionNode.prev;
        }

        // Vyzvedavani balicku z vydejen
        if (this._pkgList.size < Player.PKG_MAX_CNT) {
            ParcelManager.instance.dispensers.forEach((dispenser: Dispenser) => {
                if ((this._pkgDispenserMask & (1 << dispenser.id)) == 0) {
                    let dispMinPos = dispenser.worldPos - Dispenser.PICKUP_AREA_SIZE / 2;
                    let dispMaxPos = dispMinPos + Dispenser.PICKUP_AREA_SIZE;

                    if (this._pos.z > dispMinPos && this._pos.z < dispMaxPos) {
                        // Nabrat balicek z vydejny
                        let pkg = dispenser.pickupParcel(this);

                        if (pkg) {
                            this._pkgList.add(pkg);
                            this._pkgDispenserMask |= (1 << dispenser.id);

                            // Vyvolat udalost
                            Gameplay.instance.events.fire(Character.EVENT_PACKAGE_PICKUP, this);
                        }
                    }
                }
            });
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateDeath(): void {
        let time = TIMER.time - this._time;
        let cycle = Math.floor(time / 750);

        if (cycle >= 3) {
            this._state = eCharacterState.inactive;
            this._entity.enabled = false;
            return;
        }

        let progress = (time % 750) / 750;
        let cycleDis = 0.5;
        let y = Character.WALK_Y + cycle * cycleDis;

        this._pos.y = y + pc.BackOut(progress) * cycleDis;
        this._entity.setPosition(this._pos);

        ModelUtils.setOpacity(this._entity.model, 0.5 - (time / (750 * 3)) * 0.5);

        // EMOJI
        this._emoji.setLocalPosition(rnd.randomIn(-0.01, 0.01), Character.EMOJI_OFFSET_Y + rnd.randomIn(-0.01, 0.01), rnd.randomIn(-0.01, 0.01));
        this._emoji.element.opacity = 1 - time / (750 * 3);

        /*let progress = (TIMER.time - this._time) / 750;

        if (progress >= 1) {
            this._state = eCharacterState.inactive;
            this._entity.enabled = false;

            return;
        }

        ModelUtils.setOpacity(this._entity.model, 1 - progress);*/
    }

    //-------------------------------------------------------------------------
    /**
     * Aktualizace pozice kolizniho obdelniku
     */
    //-------------------------------------------------------------------------
    private updateColRect(): void {
        if (this._dir > 0) {
            this._colRect.y = this._pos.z + Character.COLRC_Y;
        }
        else {
            this._colRect.y = this._pos.z - Character.COLRC_Y - Character.COLRC_H;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Zpracovat zmenu stavu hrace
     * @param newState 
     */
    //-------------------------------------------------------------------------
    private handlePlStateChange(newState: ePlayerState): void {
        if (newState == ePlayerState.walk) {
            this._state = eCharacterState.walk;
        }
        else if (newState == ePlayerState.idle) {
            if (this._state == eCharacterState.walk)
                this._state = eCharacterState.idle;
        }
    }
}